/**
 * Bandz offline data layer.
 *
 * This module turns the SPA into an offline-first app at the DATA level:
 *
 *  - `cache`   IndexedDB store: successful GET responses, keyed by the request
 *              URL path (e.g. `/api/team?band=xyz`). Served verbatim when the
 *              app is offline so the UI keeps showing the last known data.
 *  - `pending` IndexedDB store: mutations (POST/PUT/PATCH/DELETE) recorded
 *              while offline. Persisted — never lost on reload or tab close.
 *              Replayed in insertion order on reconnect / manual "Sync now".
 *
 * Connectivity model
 * ------------------
 * The app is considered offline when EITHER the browser says so
 * (`navigator.onLine === false` / the `offline` event) OR a `fetch()` call
 * fails with a network `TypeError` (DNS, connection refused, airplane mode...).
 * api.ts catches that TypeError, calls `setOfflineMode(true)` and retries the
 * request through the offline path. Recovery happens via the `online` event,
 * a periodic `/api/health` probe (covers the "server came back but the browser
 * never fired online" case) and the manual "Sync now" button.
 *
 * Storage
 * -------
 * IndexedDB in real browsers (durable, async). A plain in-memory Map backend
 * is used when IndexedDB is unavailable (private mode, node test env) — the
 * API surface is identical, only persistence across reloads is lost there.
 *
 * Known limitations (documented, by design)
 * -----------------------------------------
 *  - POST endpoints now honour a caller-supplied `id` for dedupe: venues,
 *    contacts, shows, setlists, riders and stageplots accept it (a `client_id`
 *    column or the TEXT id itself) and return the existing row instead of
 *    inserting a duplicate on replay. rider_config/musicians/crew/stageplots
 *    are global per-band upserts (`ON CONFLICT(band_id)`) so never duplicate.
 *    The queued body always carries the client id so the backend reuses it.
 *  - Aggregating GETs (`/api/calendar`, `/api/itinerary/:band`, `/api/stats`)
 *    are invalidated only when their own URL prefix matches a replayed op;
 *    otherwise they refresh on next mount (pages refetch on mount anyway).
 */
export {};

// ---------------------------------------------------------------------------
// Types & stores
// ---------------------------------------------------------------------------

const DB_NAME = 'bandz-offline';
const DB_VERSION = 1;
const CACHE_STORE = 'cache'; // key = url path, value = JSON response
const PENDING_STORE = 'pending'; // autoincrement { id, url, method, body, ts }

/** One queued mutation, ready for replay. */
export interface PendingOp {
  id: number;
  url: string;
  method: string;
  body?: unknown;
  ts: number;
}

export interface OfflineState {
  offline: boolean;
  pendingCount: number;
}

interface StorageBackend {
  get(store: string, key: unknown): Promise<unknown>;
  getAll(store: string): Promise<unknown[]>;
  put(store: string, key: unknown, value: unknown): Promise<void>;
  add(store: string, value: unknown): Promise<number>;
  delete(store: string, key: unknown): Promise<void>;
  deleteWhere(store: string, pred: (key: string) => boolean): Promise<void>;
  clear(store: string): Promise<void>;
  count(store: string): Promise<number>;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let offlineMode =
  typeof navigator !== 'undefined' && navigator.onLine === false;
let pendingCount = 0;
const listeners = new Set<(s: OfflineState) => void>();

function emit() {
  const snapshot: OfflineState = { offline: offlineMode, pendingCount };
  listeners.forEach((l) => l(snapshot));
}

// ---------------------------------------------------------------------------
// IndexedDB backend
// ---------------------------------------------------------------------------

function idbReq<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB error'));
  });
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(CACHE_STORE)) {
        db.createObjectStore(CACHE_STORE); // keyPath-less: put(value, key)
      }
      if (!db.objectStoreNames.contains(PENDING_STORE)) {
        db.createObjectStore(PENDING_STORE, {
          keyPath: 'id',
          autoIncrement: true,
        });
      }
    };
    req.onsuccess = () => {
      req.result.onversionchange = () => req.result.close();
      resolve(req.result);
    };
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

class IdbBackend implements StorageBackend {
  constructor(private readonly db: IDBDatabase) {}

  private os(store: string, mode: IDBTransactionMode) {
    return this.db.transaction(store, mode).objectStore(store);
  }

  get(store: string, key: unknown) {
    return idbReq(this.os(store, 'readonly').get(key as IDBValidKey));
  }
  getAll(store: string) {
    return idbReq(this.os(store, 'readonly').getAll());
  }
  put(store: string, key: unknown, value: unknown) {
    return idbReq(
      this.os(store, 'readwrite').put(value as never, key as IDBValidKey),
    ).then(() => undefined);
  }
  add(store: string, value: unknown) {
    // The pending store uses autoIncrement, so the returned key is always a
    // number (matches the StorageBackend contract).
    return idbReq(this.os(store, 'readwrite').add(value as never)) as Promise<number>;
  }
  delete(store: string, key: unknown) {
    return idbReq(
      this.os(store, 'readwrite').delete(key as IDBValidKey),
    ).then(() => undefined);
  }
  deleteWhere(store: string, pred: (key: string) => boolean) {
    return new Promise<void>((resolve, reject) => {
      const tx = this.db.transaction(store, 'readwrite');
      const os = tx.objectStore(store);
      const req = os.openCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) {
          resolve();
          return;
        }
        if (pred(String(cur.key))) cur.delete();
        cur.continue();
      };
      req.onerror = () => reject(req.error ?? new Error('cursor error'));
    });
  }
  clear(store: string) {
    return idbReq(this.os(store, 'readwrite').clear()).then(() => undefined);
  }
  count(store: string) {
    return idbReq(this.os(store, 'readonly').count());
  }
}

/** In-memory fallback (node tests, browsers without IndexedDB). */
class MemoryBackend implements StorageBackend {
  private stores = new Map<string, Map<unknown, unknown>>();
  private seq = 0;

  private map(store: string) {
    let m = this.stores.get(store);
    if (!m) {
      m = new Map();
      this.stores.set(store, m);
    }
    return m;
  }

  async get(store: string, key: unknown) {
    return this.map(store).get(key);
  }
  async getAll(store: string) {
    return [...this.map(store).values()];
  }
  async put(store: string, key: unknown, value: unknown) {
    this.map(store).set(key, value);
  }
  async add(store: string, value: unknown) {
    const id = ++this.seq;
    const rec = { ...(value as object), id };
    this.map(store).set(id, rec);
    return id;
  }
  async delete(store: string, key: unknown) {
    this.map(store).delete(key);
  }
  async deleteWhere(store: string, pred: (key: string) => boolean) {
    const m = this.map(store);
    for (const k of [...m.keys()]) if (pred(String(k))) m.delete(k);
  }
  async clear(store: string) {
    this.map(store).clear();
  }
  async count(store: string) {
    return this.map(store).size;
  }
}

let backendPromise: Promise<StorageBackend> | null = null;

function getBackend(): Promise<StorageBackend> {
  if (!backendPromise) {
    backendPromise = (async () => {
      if (typeof indexedDB === 'undefined') return new MemoryBackend();
      try {
        return new IdbBackend(await openDb());
      } catch (err) {
        // Private browsing / quota / disabled IDB: keep working in memory.
        console.warn('[bandz-offline] IndexedDB unavailable, using in-memory fallback:', err);
        return new MemoryBackend();
      }
    })();
  }
  // backendPromise is always assigned in the branch above (or already set).
  return backendPromise!;
}

// ---------------------------------------------------------------------------
// Cache helpers
// ---------------------------------------------------------------------------

/** True when a cached key should be dropped because of a mutation at `url`. */
export function cacheKeyMatches(url: string, key: string): boolean {
  const kp = key.split('?')[0];
  if (kp === url) return true; // the exact resource
  if (kp.startsWith(url + '/')) return true; // its children
  const lastSlash = url.lastIndexOf('/');
  if (lastSlash > 0) {
    const parent = url.slice(0, lastSlash + 1); // e.g. '/api/team/'
    if (parent !== '/api/') {
      const parentNoSlash = parent.slice(0, -1); // '/api/team'
      // The parent list (with or without query) and everything under it.
      if (kp === parentNoSlash || kp.startsWith(parent)) return true;
    }
  }
  return false;
}

/** Store a successful GET response (failures are non-fatal). */
export async function cachePut(url: string, value: unknown): Promise<void> {
  try {
    const b = await getBackend();
    await b.put(CACHE_STORE, url, value);
  } catch (err) {
    console.warn('[bandz-offline] cache write failed:', err);
  }
}

/** `/api/shows/:id` (detail) — id or slug, no sub-path. */
const SHOW_DETAIL_RE = /^\/api\/shows\/([^/?]+)$/;

type CachedRow = Record<string, unknown>;

/**
 * Replay the server's list filters (`server/routes/shows.js`) on the cached
 * `/api/shows` rows. Undated rows (`date === ''`) are always kept — the server
 * never hides a future/unscheduled show behind a date range.
 */
function filterCachedShows(rows: CachedRow[], search: URLSearchParams): CachedRow[] {
  const band = search.get('band');
  const status = search.get('status');
  const eventType = search.get('event_type');
  const from = search.get('from') || search.get('min_date');
  const to = search.get('to') || search.get('max_date');
  return rows.filter((s) => {
    if (
      band &&
      String(s.band_id) !== band &&
      String(s.band_slug || '') !== band
    ) return false;
    if (status && String(s.status) !== status) return false;
    if (eventType && String(s.event_type) !== eventType) return false;
    if (from && s.date && String(s.date) < from) return false;
    if (to && s.date && String(s.date) > to) return false;
    return true;
  });
}

/**
 * Offline fallback for `/api/shows?...` and `/api/shows/:id` when only the full
 * list (`/api/shows`) was preloaded. Without it, opening the Route map or a Day
 * Sheet for the first time in airplane mode answers a 503 even though every row
 * is already in the cache. See references/offline-mode-and-preload.md.
 */
async function showsCacheFallback(
  b: StorageBackend,
  url: string,
): Promise<unknown> {
  const qIndex = url.indexOf('?');
  const path = qIndex === -1 ? url : url.slice(0, qIndex);
  const isList = path === '/api/shows';
  const detail = isList ? null : SHOW_DETAIL_RE.exec(url);
  if (!isList && !detail) return undefined;
  const allShows = await b.get(CACHE_STORE, '/api/shows');
  if (!Array.isArray(allShows)) return undefined;
  const rows = allShows as CachedRow[];
  if (isList) {
    return filterCachedShows(rows, new URLSearchParams(qIndex === -1 ? '' : url.slice(qIndex + 1)));
  }
  const key = decodeURIComponent(detail![1]);
  return rows.find(
    (s) => String(s.id) === key || String(s.slug || '') === key,
  );
}

/** Read a cached GET response; `undefined` when absent. */
export async function cacheGet(url: string): Promise<unknown> {
  const b = await getBackend();
  const exact = await b.get(CACHE_STORE, url);
  if (exact !== undefined) return exact;
  // Derived reads (filtered list / detail) served from the full shows list.
  return showsCacheFallback(b, url);
}

/** Drop a cached response (used by tests / debugging). */
export async function cacheDelete(url: string): Promise<void> {
  const b = await getBackend();
  await b.delete(CACHE_STORE, url);
}

/** Invalidate every cached GET affected by a mutation at `url`. */
export async function invalidateCache(url: string): Promise<void> {
  const b = await getBackend();
  await b.deleteWhere(CACHE_STORE, (key) => cacheKeyMatches(url, key));
}

// ---------------------------------------------------------------------------
// Pending queue
// ---------------------------------------------------------------------------

/**
 * Persist a mutation for later replay and return the stored record.
 * For POSTs a client id is injected into the body (unless one is already
 * there) so backends that honour caller-supplied ids (bands, team) replay
 * exactly once — see "Known limitations" at the top of this file.
 */
export async function enqueuePending(op: {
  url: string;
  method: string;
  body?: unknown;
}): Promise<PendingOp> {
  let body = op.body;
  if (
    op.method === 'POST' &&
    body !== null &&
    typeof body === 'object' &&
    !Array.isArray(body)
  ) {
    const b = body as Record<string, unknown>;
    if (b.id === undefined) body = { ...b, id: crypto.randomUUID() };
  }
  const b = await getBackend();
  // Coalesce: a PUT/PATCH/DELETE for the same URL supersedes any earlier op
  // for that URL (the last state wins — the server resource is replaced
  // wholesale). POSTs are creations and are never coalesced.
  if (op.method === 'PUT' || op.method === 'PATCH' || op.method === 'DELETE') {
    const existing = (await b.getAll(PENDING_STORE)) as PendingOp[];
    for (const e of existing) {
      if (e.url === op.url) {
        await b.delete(PENDING_STORE, e.id as never);
      }
    }
  }
  const record: PendingOp = { id: 0, url: op.url, method: op.method, body, ts: Date.now() };
  record.id = await b.add(PENDING_STORE, record);
  pendingCount = await b.count(PENDING_STORE);
  emit();
  return record;
}

/** All queued mutations, in insertion (replay) order. */
export async function getPendingOps(): Promise<PendingOp[]> {
  const b = await getBackend();
  const ops = (await b.getAll(PENDING_STORE)) as PendingOp[];
  return ops.sort((a, z) => a.id - z.id);
}

export async function getPendingCount(): Promise<number> {
  const b = await getBackend();
  return b.count(PENDING_STORE);
}

/**
 * Replay the pending queue against the real server, in order.
 * Stops at the first failure and keeps every remaining op queued (nothing is
 * ever dropped): a failed create must not be followed by updates to its
 * still-missing server row. Each successful op invalidates the GET caches it
 * affects so the next mount refetches fresh data.
 */
export async function flushPending(): Promise<void> {
  if (flushing) return;
  const ops = await getPendingOps();
  if (ops.length === 0) return;
  flushing = true;
  try {
    for (const op of ops) {
      try {
        const res = await fetch(op.url, {
          method: op.method,
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: op.body !== undefined ? JSON.stringify(op.body) : undefined,
        });
        if (!res.ok) {
          // Server answered but refused (401 = session gone, 4xx/5xx = data
          // rejected). Keep the op queued; stop so order is preserved. A 401
          // will surface through the next real fetchJSON call, which redirects
          // to /login; after re-login apiLogin triggers flushPending() again.
          break;
        }
        await res.json().catch(() => undefined); // drain the body
        const b = await getBackend();
        await b.delete(PENDING_STORE, op.id);
        await invalidateCache(op.url);
      } catch {
        // Network still down (TypeError) or transient: stop, retry later.
        setOfflineMode(true);
        break;
      }
    }
  } finally {
    flushing = false;
    const b = await getBackend();
    pendingCount = await b.count(PENDING_STORE).catch(() => pendingCount);
    emit();
  }
}

let flushing = false;

// ---------------------------------------------------------------------------
// Connectivity state
// ---------------------------------------------------------------------------

export function getOfflineState(): OfflineState {
  return { offline: offlineMode, pendingCount };
}

export function subscribeOffline(cb: (s: OfflineState) => void): () => void {
  listeners.add(cb);
  cb(getOfflineState());
  return () => {
    listeners.delete(cb);
  };
}

/** Flip the offline flag (connectivity events, TypeError detection, tests). */
export function setOfflineMode(offline: boolean): void {
  if (offlineMode === offline) return;
  offlineMode = offline;
  emit();
  if (!offline) void flushPending();
}

/** True when a fetch() failure is a network-level error (offline/DNS/refused). */
export function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError;
}

/**
 * Wipe the local cache and pending queue (debugging / "clear local data").
 * The pending queue is never cleared automatically — only on explicit call.
 */
export async function clearOfflineData(): Promise<void> {
  const b = await getBackend();
  await b.clear(CACHE_STORE);
  await b.clear(PENDING_STORE);
  pendingCount = 0;
  offlineMode = typeof navigator !== 'undefined' && navigator.onLine === false;
  emit();
}

// ---------------------------------------------------------------------------
// Connectivity wiring (browser only)
// ---------------------------------------------------------------------------

let probeTimer: ReturnType<typeof setInterval> | null = null;

async function probeConnectivity(): Promise<void> {
  try {
    // /api/health is public (no auth). ANY HTTP response proves the server is
    // reachable — even 401/500 mean the network is up, so we go back online
    // and let the queue replay (failed ops simply stay queued).
    const res = await fetch('/api/health', {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
    });
    if (res.status >= 200 && res.status < 500) setOfflineMode(false);
  } catch {
    /* still offline */
  }
}

function ensureProbe() {
  if (probeTimer) return;
  probeTimer = setInterval(() => {
    if (!offlineMode) {
      clearInterval(probeTimer!);
      probeTimer = null;
      return;
    }
    // If the browser already reports "offline", the `online` event will fire
    // on recovery — the probe only covers silent recoveries.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    void probeConnectivity();
  }, 20000);
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => setOfflineMode(false)); // triggers flush
  window.addEventListener('offline', () => setOfflineMode(true));
  ensureProbe();

  // Startup: hydrate the pending count, then replay anything queued from a
  // previous session as soon as the network is up.
  void (async () => {
    await getBackend();
    const b = await getBackend();
    pendingCount = await b.count(PENDING_STORE).catch(() => 0);
    emit();
    if (typeof navigator === 'undefined' || navigator.onLine !== false) {
      void flushPending();
    }
  })();
}
